<!--
  A recorded payment is a magnitude, not a signed flow: like a transfer (AGENTS.md rule 4),
  its direction is unambiguous from context (a payment row, in a payment history) so it
  renders unsigned rather than manufacturing a sign no other figure here carries.
-->
<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { formatDate } from '@taler/domain'
import { Card } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { LoanPaymentRow } from '../internal/payment-history.ts'

const props = defineProps<{ payments: readonly LoanPaymentRow[] }>()
const { locale, t } = useI18n()

const rows = computed(() =>
  props.payments.map((payment) => ({
    key: payment.entryId,
    date: formatDate(payment.date, locale.value as 'en' | 'hu'),
    amount: payment.amount,
    principal: payment.principalPortion,
    interest: payment.interestPortion,
    overpayment: payment.overpaymentPortion,
  })),
)
</script>

<template>
  <Card padding="none" class="overflow-hidden">
    <p v-if="rows.length === 0" class="p-[var(--space-group)] text-[color:var(--color-text-muted)]">
      {{ t('loans.noPayments') }}
    </p>

    <table v-else class="w-full border-collapse">
      <thead>
        <tr class="table-head">
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('loans.paymentDate') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)] text-right" scope="col">
            {{ t('loans.paymentAmount') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)] text-right" scope="col">
            {{ t('loans.principalPortion') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)] text-right" scope="col">
            {{ t('loans.interestPortion') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)] text-right" scope="col">
            {{ t('loans.overpaymentPortion') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row.key"
          class="table-row border-b border-[color:var(--color-border)] last:border-b-0"
        >
          <td
            class="h-[var(--density-row)] px-[var(--space-cell)] align-middle tabular-nums text-[color:var(--color-text)]"
          >
            {{ row.date }}
          </td>
          <td class="h-[var(--density-row)] px-[var(--space-cell)] text-right align-middle">
            <MoneyAmount :value="row.amount" />
          </td>
          <td class="h-[var(--density-row)] px-[var(--space-cell)] text-right align-middle">
            <MoneyAmount :value="row.principal" />
          </td>
          <td class="h-[var(--density-row)] px-[var(--space-cell)] text-right align-middle">
            <MoneyAmount :value="row.interest" />
          </td>
          <td class="h-[var(--density-row)] px-[var(--space-cell)] text-right align-middle">
            <MoneyAmount :value="row.overpayment" />
          </td>
        </tr>
      </tbody>
    </table>
  </Card>
</template>
